import type { Breadcrumb, BreadcrumbHint } from '@sentry/react';
import { isSameOrigin } from './Url';

const REQUEST_ID_HEADER = 'x-request-id';

/**
 * Adds the backend's request id to the breadcrumbs of fetch and XHR requests,
 * so that an error lists the backend requests before it and each one can be
 * found in the backend logs by `@request_id`.
 */
export function withBackendRequestId(
    breadcrumb: Breadcrumb,
    hint?: BreadcrumbHint,
): Breadcrumb {
    const requestId = backendRequestId(breadcrumb, hint);
    if (requestId === null) {
        return breadcrumb;
    }
    return {
        ...breadcrumb,
        data: { ...breadcrumb.data, request_id: requestId },
    };
}

function backendRequestId(
    breadcrumb: Breadcrumb,
    hint: BreadcrumbHint | undefined,
): string | null {
    // the browser refuses, with a console warning, to read the header of
    // another origin's response
    const url: unknown = breadcrumb.data?.url;
    if (typeof url !== 'string' || !isSameOrigin(url)) {
        return null;
    }
    switch (breadcrumb.category) {
        case 'fetch':
            return hint?.response instanceof Response
                ? hint.response.headers.get(REQUEST_ID_HEADER)
                : null;
        case 'xhr':
            return hint?.xhr instanceof XMLHttpRequest
                ? hint.xhr.getResponseHeader(REQUEST_ID_HEADER)
                : null;
        default:
            return null;
    }
}
